import React, { useCallback, useState } from 'react'

import cx from 'classnames'
import { changeFilterGroups } from 'slices/filters'

import { Flex, Icon, Tag, Tooltip } from '@driftt/dds'
import { faCircleInfo } from '@driftt/dds.icons/light/faCircleInfo'
import { Button, Div, Label } from '@driftt/tide-core'
import { access } from '@driftt/traffic-control'

import { DeserializedSavedFilter } from 'api/AccountActivityAPI/types'
import FiltersForm from 'components/FiltersForm'
import { GlobalFilterCondition, GlobalFilterSettingType } from 'hooks/useAdvancedPreferences'
import { useAttributeFilters } from 'hooks/useAttributeFilters'
import useFilterConfiguration from 'hooks/useFilterConfiguration'
import { useGlobalFilters } from 'hooks/useGlobalFilters'
import { useOnClickOutsideWithPortals } from 'hooks/useOnClickOutside'
import { useAppDispatch, useAppSelector } from 'hooks/useRedux'
import { defaultFilterTypes, FilterGroup, FilterOperator, FilterPage } from 'utils/filters'

import styles from './styles.module.css'

interface FilterCardProps {
  onClose: () => void
  onOpenEditModal: (filter?: DeserializedSavedFilter, filterGroups?: FilterGroup[]) => void
  setActiveFilterId: (id: number | undefined) => void
  useAccountFilters?: boolean
  useDefaultStyles?: boolean
}

const CCRMFiltersCard: React.FC<FilterCardProps> = ({
  onClose,
  onOpenEditModal,
  setActiveFilterId,
  useAccountFilters = false,
  useDefaultStyles = false
}) => {
  const globalFilters = useGlobalFilters()
  const [refElement, setRefElement] = useState<HTMLElement | null>(null)
  const dispatch = useAppDispatch()
  const { filterGroupsByPage } = useAppSelector(({ filters }) => filters)
  const filterGroups = filterGroupsByPage[FilterPage.ACTIVITIES]
  const [updatedFilterGroups, setUpdatedFilterGroups] = useState(filterGroups)

  const attributeFilters = useAttributeFilters()
  const filterConfiguration = useFilterConfiguration(attributeFilters, defaultFilterTypes)
  const onChangeFilterGroups = useCallback(
    (filterGroups: FilterGroup[], page: FilterPage) => {
      dispatch(changeFilterGroups({ filterGroups, page }))
    },
    [dispatch]
  )
  const handleApplyFilters = useCallback(() => {
    setActiveFilterId(0)
    onChangeFilterGroups(updatedFilterGroups, FilterPage.ACTIVITIES)
    if (useAccountFilters) {
      onChangeFilterGroups(updatedFilterGroups, FilterPage.ACCOUNTS)
    }
    onClose()
  }, [useAccountFilters, updatedFilterGroups, onClose, onChangeFilterGroups, setActiveFilterId])

  const handleSaveFilters = useCallback(() => {
    onOpenEditModal(undefined, updatedFilterGroups)
    onClose()
  }, [onClose, onOpenEditModal, updatedFilterGroups])

  const handleChangeFilterGroups = useCallback((filterGroups: FilterGroup[]) => {
    setUpdatedFilterGroups(filterGroups)
  }, [])

  useOnClickOutsideWithPortals(refElement, (e: MouseEvent) => {
    e.stopPropagation()
    onClose()
  })

  const handleClose = () => {
    setActiveFilterId(0)
    onChangeFilterGroups([], FilterPage.ACTIVITIES)
    onChangeFilterGroups([], FilterPage.ACCOUNTS)
    onClose()
  }

  const hasNewPreferencesGate = access.hasGates('prospector_new_preferences_ui')
  const showGlobalFilterTags = hasNewPreferencesGate && globalFilters.length > 0

  return (
    <div
      ref={setRefElement}
      className={cx(styles.cardStyle, {
        [styles.defaultCardStyle]: useDefaultStyles
      })}>
      {showGlobalFilterTags && 
        <>
          <Label>
            <Flex alignItems='center'>
              Global filters
              <Tooltip content='Global filters are set by your Drift admin.' side='top'>
                <Icon icon={faCircleInfo} size='small'/>
              </Tooltip>
            </Flex>
          </Label>
          <Flex css={{marginBottom: '8px'}}>
            {globalFilters.map((filter, index) => {
              let filterLabel = 'In CRM is true'
              if(filter.name !== GlobalFilterSettingType.IN_CRM){
                const filterCondition = filter.value as GlobalFilterCondition
                //global filters are exlusionary, so we should swap the operator that's in the setting
                const operator = filterCondition.filterOperation === FilterOperator.EQUALS ? 'is not' : 'is'
                const values = filterCondition.values.join(', ')
                filterLabel = `${filterCondition.attribute} ${operator} ${values}`
              }
              return (
                <Tooltip content={filterLabel} key={index}>
                    <Tag variant='gray' >
                      {filterLabel}
                    </Tag>
                  </Tooltip>
              )
            })
            }
          </Flex>
        </>
      }
      
      <Label>Filter</Label>
      <Div className={styles.cardForm}>
        <FiltersForm
          initialGroups={filterGroups}
          onChangeFilterGroups={handleChangeFilterGroups}
          filterConfiguration={filterConfiguration}
          onClose={handleClose}
        />
      </Div>
      <Div className={styles.footerContainer}>
        <Div>
          <Button onClick={handleApplyFilters} type="tertiary" className={styles.marginRight}>
            Apply
          </Button>
          <Button onClick={onClose} type="tertiary">
            Cancel
          </Button>
        </Div>
        <Button onClick={handleSaveFilters} type="primary">
          Save filter
        </Button>
      </Div>
    </div>
  )
}

export default CCRMFiltersCard
